/**
 * Executes stored auth intents that may need async work before navigation.
 *
 * `resolveAuthIntent()` remains the pure routing contract. This module layers
 * auth-completion side effects on top of that contract so web callback,
 * signup completion, and native post-auth all share the same behavior.
 */
import type { QueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  acceptSpaceInvitation,
  setEventGoingApiV1EventsEventIdGoingPost,
} from '@api-client/api-client';
import {
  invalidateAcceptedInvitationQueries,
  sweepAfterFailedAccept,
} from '@hooks/useSpaceInvitations';
import { routes } from '@routes';
import {
  AuthIntent,
  leftViaAddEvent,
  resolveAuthIntent,
  type AuthIntentPayload,
} from '@utils/authIntent';
import {
  buildAttendToast,
  buildPinSheetFailedToast,
  isReadableAttendSummary,
  pinRequiredOf,
  requestedSpacesAreComplete,
  requestedSpacesOf,
} from '@utils/attendSummary';
import { buildRegistrationCelebration } from '@utils/registrationCelebration';
import type { IntentDraft } from '@lib/overlay/intents';
import { invalidateGoingEventQueries } from '@utils/invalidateGoingEventQueries';
import type { SpaceMembershipChange } from '@utils/invalidateSpaceMembershipQueries';
import { isEventCancelledError } from '@utils/eventStatus';
import { logger } from '@utils/logger';
import { sanitizeError } from '@utils/sanitizeError';
import { toast } from '@hooks/use-toast';

/** Recovery states surfaced to invite UI after post-auth execution. */
export type InviteRecoveryReason = 'email-mismatch' | 'expired';

/** Location state emitted by the post-auth executor for recovery UI. */
export interface PostAuthIntentExecutionState {
  inviteRecovery?: { reason: InviteRecoveryReason };
}

/** Final navigation target returned after auth-intent execution completes. */
export interface PostAuthIntentExecutionResult {
  path: string;
  state?: PostAuthIntentExecutionState;
  /** Overlay intents to post, in order, immediately before `navigate()`. Absent when the intent
   * kind never queues one, or the underlying action (invite accept, event attend) failed. */
  intents?: IntentDraft[];
}

/** Dependencies required to execute a stored auth intent. */
interface ExecutePostAuthIntentArgs {
  intent: AuthIntentPayload;
  redirectPath: string | null;
  queryClient: QueryClient;
  accessToken?: string | null;
}

/** Builds the clean community URL used after invite acceptance succeeds. */
function getAcceptedInvitePath(
  communitySlug: string,
  { events }: { events?: boolean } = {},
): string {
  return routes.community.details(
    { id: null, slug: communitySlug },
    { returnTo: routes.events(), ...(events ? { query: { tab: 'events' } } : {}) },
  );
}

/**
 * Executes a stored auth intent and returns the next navigation target.
 *
 * Most intents are route-only and delegate directly to `resolveAuthIntent()`.
 * Two kinds do async work after auth before navigating:
 * - `GOING_EVENT` finishes the "Going" RSVP (attend + auto-join the event's spaces).
 * - `ACCEPT_SPACE_INVITE` performs the invite accept, invalidates membership caches, and
 *   maps known failures to recovery state for manual invite UI. When the stored redirect path carries
 *   the Add Event marker, a successful (or 409) accept lands on the events tab with a `create-event`
 *   intent instead of the welcome intent.
 *
 * @param args.intent Stored auth intent payload.
 * @param args.redirectPath Stored pre-auth redirect path, if any.
 * @param args.queryClient Query client used for invalidation after mutations.
 * @param args.accessToken Access token for authenticated post-auth API calls.
 */
export async function executePostAuthIntent({
  intent,
  redirectPath,
  queryClient,
  accessToken,
}: ExecutePostAuthIntentArgs): Promise<PostAuthIntentExecutionResult> {
  if (intent.kind === AuthIntent.GOING_EVENT) {
    return executeGoingEvent({ intent, redirectPath, queryClient, accessToken });
  }

  if (intent.kind !== AuthIntent.ACCEPT_SPACE_INVITE) {
    return resolveAuthIntent(intent, redirectPath);
  }

  const fallbackTarget = resolveAuthIntent(intent, redirectPath);

  if (!accessToken) {
    logger.error('[executePostAuthIntent] Missing access token for invite acceptance fallback');
    return fallbackTarget;
  }

  // A signed-out invitee who tapped Add Event is accepted here, before landing, so the add-event drawer
  // (the Space screen consumes `create-event`) follows instead of the "You are in!" drawer.
  const fromAddEvent = leftViaAddEvent(redirectPath, intent.communitySlug);
  const addEventTarget: PostAuthIntentExecutionResult = {
    path: getAcceptedInvitePath(intent.communitySlug, { events: true }),
    intents: [{ surface: 'create-event', payload: { community: intent.communitySlug } }],
  };

  try {
    const { data: accepted } = await acceptSpaceInvitation(
      intent.communitySlug,
      { token: intent.invitationToken },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );

    invalidateAcceptedInvitationQueries(queryClient, intent.communitySlug, intent.invitationToken, {
      kind: 'these',
      ids: [accepted.space_id],
    });

    if (fromAddEvent) return addEventTarget;

    return {
      path: getAcceptedInvitePath(intent.communitySlug),
      intents: [{ surface: 'community-welcome', payload: { community: intent.communitySlug } }],
    };
  } catch (error) {
    // Handle both AxiosError (production) and plain error objects (tests, non-Axios callers)
    const fallbackError = error as { response?: { status?: number } };
    const status = isAxiosError(error) ? error.response?.status : fallbackError.response?.status;

    if (status === 403) {
      return {
        ...fallbackTarget,
        state: { inviteRecovery: { reason: 'email-mismatch' } },
      };
    }

    if (status === 410) {
      return {
        ...fallbackTarget,
        state: { inviteRecovery: { reason: 'expired' } },
      };
    }

    sweepAfterFailedAccept(queryClient, intent.communitySlug, intent.invitationToken, status);

    if (status === 409) {
      return fromAddEvent ? addEventTarget : { path: getAcceptedInvitePath(intent.communitySlug) };
    }

    logger.error('[executePostAuthIntent] Failed to execute auth intent:', sanitizeError(error));
    return fallbackTarget;
  }
}

/**
 * Finishes a "Going" RSVP after auth: POSTs the attend (RSVP + auto-join the event's spaces),
 * shows the joined-spaces summary toast, refreshes the affected caches, and lands the user back on
 * the event page (resolveAuthIntent returns the stored redirect, else routes.event.details).
 *
 * Best-effort by design: a failed attend (missing token, 404, network) is logged and the user
 * is still routed to the fallback target, never blocked or shown an alarming error on their
 * very first authenticated screen. They can re-tap "Going" in-app to retry. A rejected POST
 * still sweeps the caches (it may have committed); a missing token sends nothing and sweeps nothing.
 */
async function executeGoingEvent({
  intent,
  redirectPath,
  queryClient,
  accessToken,
}: {
  intent: Extract<AuthIntentPayload, { kind: typeof AuthIntent.GOING_EVENT }>;
  redirectPath: string | null;
  queryClient: QueryClient;
  accessToken?: string | null;
}): Promise<PostAuthIntentExecutionResult> {
  const fallbackTarget = resolveAuthIntent(intent, redirectPath);

  if (!accessToken) {
    logger.error('[executePostAuthIntent] Missing access token for going-event attend');
    return fallbackTarget;
  }

  try {
    const { data } = await setEventGoingApiV1EventsEventIdGoingPost(intent.eventId, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    // ONE readability decision for the whole block, mirroring the share pipeline's G6 guard. Every
    // line below indexes into the four buckets, and `buildAttendToast` measures three of them, so a
    // 201 with a truthy but malformed body would throw here — into the catch at the bottom. An
    // unreadable body is not evidence the attend did nothing, so it takes the honest branch
    // instead: no toast (there is no summary to report), and a membership outcome of `unknown`.
    const readable = isReadableAttendSummary(data);
    // TECH-663: a host Space with a PIN. The PIN sheet confirms the Going itself, so no toast for
    // joined or pending; a Space that failed is on no card, so it still gets its warning.
    const pins = readable ? pinRequiredOf(data) : [];
    if (readable) {
      const attendToast =
        pins.length === 0 ? buildAttendToast(data) : buildPinSheetFailedToast(data);
      if (attendToast) toast(attendToast);
    } else {
      logger.warn('[executePostAuthIntent] Attend response was unreadable; sweeping wide');
    }
    // EVERY post-auth attend celebrates (design §4.2) — the once-ever guard in
    // CelebrationHost prevents replays of the plain one; `source` rides along for analytics only.
    // Returned rather than posted here: this runs before the caller knows whether a 'signup'
    // celebration also needs to queue ahead of it (SignupFlow, on a full-profile signup), and
    // posting straight to the intent channel from here can win a race against that later post and
    // land in the wrong queue order. The caller (resolvePostSignupDestination /
    // useCallbackRedirect / useNativePostAuth) posts this intent itself, immediately before
    // `navigate()`, in signup-then-attend order — see SignupFlow.tsx's post site.
    // Fire-and-forget: this path is best-effort, so refresh the affected caches in the background
    // instead of blocking the user's first authed navigation on the refetches settling.
    // Shared sweep (design §4.1). The GOING_EVENT intent payload only carries the event id, but
    // the response names every space the event touches (review F2): every bucket. Only
    // `joined` + `pending` + `pin_required` changed membership (`skipped` is the ordinary
    // already-a-member re-attend, `failed` rolled its write back), so the rosters take the
    // narrower set — `these` when that set is non-empty, `none` when it isn't (an attend that
    // landed entirely in `skipped` / `failed`).
    //
    // That `none` is a stronger claim than it looks, and it holds HERE for a reason that does not
    // travel: `skipped` covers "already a member" (backend `_enroll_one` returns it for any
    // existing non-DECLINED membership), so on a transport that RETRIES, attempt 1's committed
    // join hides behind attempt 2's `skipped` and the empty `joined + pending` is a lie. This path
    // makes a single un-retried request — a direct axios call, not a React Query mutation, and the
    // only re-issue the axios layer performs is the response interceptor's 401 token refresh,
    // which re-sends a request the server rejected before touching a row. `useToggleGoingEvent`
    // runs the same mapping under `authAwareRetry` and therefore has to detect the retry
    // (see its `membershipChangeOf`). If this ever moves onto a retrying transport, that detection
    // has to come with it.
    //
    // `unknown` DOES apply here, on two inputs: a 201 whose body the guard above could not read,
    // and (in the catch below) a rejected POST, which may still have committed.
    // `requestedSpacesOf` is `pending` plus the PIN Spaces (TECH-663 `pin_required`), PENDING rows
    // the backend reports in their own bucket, so they changed membership just as `pending` did.
    const requested = readable ? requestedSpacesOf(data) : [];
    const changedSpaceIds = readable
      ? [...data.joined, ...requested].map(space => space.space_id)
      : [];
    const namedSpaceIds = readable
      ? [...data.joined, ...requested, ...data.skipped, ...data.failed].map(space => space.space_id)
      : [];
    // An unreadable body names no space, so no roster is swept — but the global membership
    // surfaces (my-joined, community list, community detail) all fire, because "I cannot read the
    // response" is not "nothing happened". A readable body whose `pin_required` lists an item
    // `requestedSpacesOf` had to drop is not a complete claim either (a request went to a Space it
    // no longer names): `unknown`, over every Space it does name.
    const membershipChange: SpaceMembershipChange = !readable
      ? { kind: 'unknown', candidateIds: [] }
      : !requestedSpacesAreComplete(data)
        ? { kind: 'unknown', candidateIds: namedSpaceIds }
        : changedSpaceIds.length === 0
          ? { kind: 'none' }
          : { kind: 'these', ids: changedSpaceIds };
    invalidateGoingEventQueries(queryClient, {
      id: intent.eventId,
      spaceIds: namedSpaceIds,
      membershipChange,
    });

    // When the server asks for a ticket (TECH-575), the celebration that comes back is the
    // ticketed one, in place of the plain event_going celebration: it is the same moment, with
    // the question in it. Like the plain one, it marks the first-Going carousel complete on show.
    const registration = readable ? buildRegistrationCelebration(data, 'post_auth') : null;
    const source = intent.source ?? null;
    if (pins.length > 0) {
      // TECH-663: the PIN sheet in place of either celebration. It carries the ticket question as
      // its last step, and like them it marks the first-Going carousel complete on show.
      return {
        ...fallbackTarget,
        intents: [
          {
            surface: 'celebration',
            payload: {
              kind: 'event_pin',
              source,
              eventPin: {
                event_id: intent.eventId,
                spaces: pins,
                registration,
                surface: 'post_auth',
              },
            },
          },
        ],
      };
    }
    return {
      ...fallbackTarget,
      intents: [
        {
          surface: 'celebration',
          payload: registration
            ? { kind: 'registration_check', source, registration }
            : { kind: 'event_going', source },
        },
      ],
    };
  } catch (error) {
    // A Going saved before sign-in can meet an event cancelled since (409 EVENT_CANCELLED): that is
    // the stale page the guest left, not a failure, so it lands on the cancelled page without a
    // toast or an error log (the invalidation below refetches it).
    if (!isEventCancelledError(error)) {
      logger.error(
        '[executePostAuthIntent] Failed to attend event after auth:',
        sanitizeError(error),
      );
    }
    // The POST may have committed (#1058) and the intent carries no space ids, so candidateIds is
    // empty.
    invalidateGoingEventQueries(queryClient, {
      id: intent.eventId,
      spaceIds: [],
      membershipChange: { kind: 'unknown', candidateIds: [] },
    });
    return fallbackTarget;
  }
}
